import { useState } from "react";
import { X, Search, Loader2, MapPin, Package, Truck, CheckCircle2, Clock } from "lucide-react";
import api, { formatDate } from "../lib/api";

const STATUS_LABEL = {
  created: "Booked", in_transit: "In transit", delivered: "Delivered",
  epod: "Delivered", inv_generated: "Delivered", inv_submitted: "Delivered",
  paid: "Delivered", cancelled: "Cancelled",
};

export function TrackConsignment({ open, onClose }) {
  const [num, setNum] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  if (!open) return null;

  async function track(e) {
    e.preventDefault();
    const q = num.trim();
    if (!q) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/p/track-lr/${encodeURIComponent(q)}`);
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not track that consignment.");
    } finally {
      setLoading(false);
    }
  }

  function close() {
    onClose();
    setTimeout(() => { setNum(""); setResult(null); setError(""); }, 250);
  }

  const status = result ? (STATUS_LABEL[result.status] || result.status || "Booked") : "";
  const delivered = result && ["delivered", "epod", "inv_generated", "inv_submitted", "paid"].includes(result.status);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="track-modal">
      <div className="absolute inset-0 bg-[#1A1A18]/70 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-lg bg-[#F9F8F6] rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
        <button onClick={close} data-testid="track-close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-[#1A1A18]/5 hover:bg-[#1A1A18]/10 flex items-center justify-center text-[#1A1A18]">
          <X className="w-5 h-5" />
        </button>

        <div className="p-7 sm:p-9">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#C84B31] mb-2">Track your consignment</p>
          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-[#1A1A18] leading-tight">Where's my shipment?</h3>
          <p className="mt-2 text-sm text-[#4F4C44]">Enter your Lorry Receipt (LR) number to see live status.</p>

          <form onSubmit={track} className="mt-5 flex gap-2">
            <input data-testid="track-input" value={num} onChange={(e) => setNum(e.target.value)}
              placeholder="e.g. LR-20260012"
              className="flex-1 rounded-xl border border-[#1A1A18]/15 bg-white px-4 py-3 text-sm text-[#1A1A18] focus:border-[#C84B31] focus:ring-1 focus:ring-[#C84B31] outline-none" />
            <button type="submit" disabled={loading || !num.trim()} data-testid="track-submit"
              className="inline-flex items-center gap-2 rounded-xl bg-[#C84B31] px-5 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Track
            </button>
          </form>

          {error && <p className="mt-4 text-sm text-[#C84B31]" data-testid="track-error">{error}</p>}

          {result && (
            <div className="mt-6" data-testid="track-result">
              <div className="rounded-xl bg-white border border-[#1A1A18]/10 p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-heading font-bold text-lg text-[#1A1A18]">{result.lr_number}</span>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${delivered ? "bg-[#EAF2ED] text-[#2D523B]" : "bg-[#FCECD8] text-[#915A1A]"}`}>
                    {delivered ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Truck className="w-3.5 h-3.5" />} {status}
                  </span>
                </div>
                <div className="mt-4 flex items-center gap-3 text-sm">
                  <span className="flex items-center gap-1.5 text-[#1A1A18]"><MapPin className="w-4 h-4 text-[#C84B31]" /> {result.from_location || result.from_label || "—"}</span>
                  <span className="text-[#68665E]">→</span>
                  <span className="flex items-center gap-1.5 text-[#1A1A18]"><MapPin className="w-4 h-4 text-[#2D523B]" /> {result.to_location || result.to_label || "—"}</span>
                </div>
                {result.goods_description && (
                  <div className="mt-2 flex items-center gap-1.5 text-sm text-[#4F4C44]"><Package className="w-4 h-4 text-[#C84B31]" /> {result.goods_description}</div>
                )}
              </div>

              {result.timeline?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs font-semibold uppercase tracking-widest text-[#68665E] mb-3">Journey</div>
                  <ol className="relative border-l border-[#E5E3DB] ml-2 space-y-4">
                    {result.timeline.map((t, i) => (
                      <li key={i} className="ml-4">
                        <span className="absolute -left-[7px] mt-1 w-3 h-3 rounded-full bg-[#C84B31] border-2 border-[#F9F8F6]" />
                        <div className="text-sm text-[#1A1A18]">{t.text}</div>
                        {t.time && <div className="text-xs text-[#68665E] flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {formatDate(t.time)}</div>}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
